import { useState } from "react";
import { ENGINE_LABELS, type Engine } from "@velivo/shared";
import { Icon } from "../Icon";

/**
 * FR-DASH-02 — the Narrative Pillar × Engine heatmap.
 *
 * Two things this component refuses to do, both of which would quietly break
 * the customer's trust in the number:
 *
 * 1. Render an unmeasured cell as zero. A pillar/engine pair with no scored
 *    runs is hatched grey and labelled, never a green "Aligned" the customer
 *    never earned (nor a red crisis that doesn't exist).
 * 2. Shrink into a table on mobile. DESIGN_SYSTEM.md §6 requires a genuinely
 *    different component below tablet width — a stacked list of "pillar, worst
 *    engine" cards — so both layouts are built here rather than one retrofitted.
 */

export type Severity = "aligned" | "erosion" | "critical";

export interface MatrixCell {
  engine: Engine;
  ndi: number | null;
  mptr: number | null;
  samples: number;
  severity: Severity | null;
  lastComputedAt: string | null;
}

export interface MatrixPillar {
  pillarId: string;
  name: string;
  cells: MatrixCell[];
}

export interface MatrixPayload {
  engines: Engine[];
  pillars: MatrixPillar[];
  window: { days: number };
}

const SEVERITY_RANK: Record<Severity, number> = {
  critical: 0,
  erosion: 1,
  aligned: 2,
};

export function AlignmentMatrix({
  data,
  metric,
  onCell,
}: {
  data: MatrixPayload | null;
  metric: "ndi" | "mptr";
  onCell: (pillarId: string, engine: Engine) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (!data) {
    return (
      <div className="skeleton-stack">
        {[0, 1, 2].map((i) => (
          <div className="skeleton" key={i} style={{ height: 56 }} />
        ))}
      </div>
    );
  }

  const label = (cell: MatrixCell) =>
    cell.severity === null
      ? "Not measured"
      : `${metric === "ndi" ? "NDI" : "MPTR"} ${metric === "ndi" ? cell.ndi : cell.mptr}, ${cell.severity}, ${cell.samples} scored ${cell.samples === 1 ? "response" : "responses"}`;

  const value = (cell: MatrixCell) =>
    cell.severity === null
      ? "—"
      : metric === "ndi"
        ? cell.ndi
        : `${cell.mptr}%`;

  return (
    <>
      <div className="heatmap-wrap overflow-x">
        <table className="heatmap">
          <caption className="sr-only">
            Narrative drift by pillar and engine over the last{" "}
            {data.window.days} days
          </caption>
          <thead>
            <tr>
              <th scope="col">
                <span className="sr-only">Narrative Pillar</span>
              </th>
              {data.engines.map((engine) => (
                <th scope="col" key={engine}>
                  {ENGINE_LABELS[engine] ?? engine}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.pillars.map((pillar) => (
              <tr key={pillar.pillarId}>
                <th scope="row">{pillar.name}</th>
                {pillar.cells.map((cell) => (
                  <td key={cell.engine}>
                    <button
                      type="button"
                      className={`heatcell heatcell--${cell.severity ?? "none"}`}
                      onClick={() =>
                        cell.severity && onCell(pillar.pillarId, cell.engine)
                      }
                      disabled={cell.severity === null}
                      aria-label={`${pillar.name}, ${ENGINE_LABELS[cell.engine] ?? cell.engine}: ${label(cell)}`}
                      title={label(cell)}
                    >
                      <span className="heatcell__value">{value(cell)}</span>
                      <span className="heatcell__meta">
                        {cell.severity === null
                          ? "no runs"
                          : `${cell.samples} ${cell.samples === 1 ? "run" : "runs"}`}
                      </span>
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile layout — a different component, not a smaller table. */}
      <div className="heatstack">
        {data.pillars.map((pillar) => {
          const measured = pillar.cells.filter((c) => c.severity !== null);
          const worst = [...measured].sort(
            (a, b) => SEVERITY_RANK[a.severity!] - SEVERITY_RANK[b.severity!],
          )[0];
          const open = expanded === pillar.pillarId;
          return (
            <div className="heatstack__row" key={pillar.pillarId}>
              <button
                type="button"
                className="heatstack__head"
                onClick={() => setExpanded(open ? null : pillar.pillarId)}
                aria-expanded={open}
              >
                <span>
                  <strong>{pillar.name}</strong>
                  <br />
                  <span className="muted">
                    {worst
                      ? `Worst: ${ENGINE_LABELS[worst.engine] ?? worst.engine}`
                      : "No scored runs yet"}
                  </span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {worst ? (
                    <span className={`badge badge--${worst.severity}`}>
                      {value(worst)}
                    </span>
                  ) : (
                    <span className="badge badge--neutral">—</span>
                  )}
                  <Icon name="chevronDown" aria-hidden="true" />
                </span>
              </button>

              {open ? (
                <div className="heatstack__engines">
                  {pillar.cells.map((cell) => (
                    <button
                      type="button"
                      key={cell.engine}
                      className={`heatcell heatcell--${cell.severity ?? "none"}`}
                      onClick={() =>
                        cell.severity && onCell(pillar.pillarId, cell.engine)
                      }
                      disabled={cell.severity === null}
                      aria-label={`${ENGINE_LABELS[cell.engine] ?? cell.engine}: ${label(cell)}`}
                    >
                      <span className="heatcell__meta">
                        {ENGINE_LABELS[cell.engine] ?? cell.engine}
                      </span>
                      <span className="heatcell__value">{value(cell)}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="heatmap__legend">
        <span className="heatmap__legenditem">
          <span
            className="heatmap__swatch"
            style={{ background: "var(--color-aligned-tint)" }}
            aria-hidden="true"
          />
          Aligned
        </span>
        <span className="heatmap__legenditem">
          <span
            className="heatmap__swatch"
            style={{ background: "var(--color-erosion-tint)" }}
            aria-hidden="true"
          />
          Erosion
        </span>
        <span className="heatmap__legenditem">
          <span
            className="heatmap__swatch"
            style={{ background: "var(--color-critical-tint)" }}
            aria-hidden="true"
          />
          Critical Drift
        </span>
        <span className="heatmap__legenditem">
          <span className="heatmap__swatch heatcell--none" aria-hidden="true" />
          Not measured in this window
        </span>
      </div>
    </>
  );
}
